import { safeSessionStorage, safeStorage } from './safe-storage';

const CHUNK_LOAD_ERROR_RELOAD_AT_KEY = '__chunk_load_error_reload_at__';
const CHUNK_LOAD_ERROR_RELOAD_COOLDOWN = 5 * 60 * 1000;
const CHUNK_LOAD_ERROR_SESSION_RELOAD_KEY = '__chunk_load_error_reloaded_in_session__';

type WindowLike = Pick<Window, 'addEventListener' | 'removeEventListener' | 'location'>;

export const isChunkLoadError = (message?: string | null): boolean => {
  if (!message) {
    return false;
  }

  return [
    /module factory is not available/i,
    /ChunkLoadError/i,
    /Loading chunk [^\s]+ failed/i,
    /Loading CSS chunk [^\s]+ failed/i,
  ].some((pattern) => pattern.test(message));
};

const getNow = () => Date.now();

const getLastReloadAt = (): number => {
  const value = safeStorage.getItem(CHUNK_LOAD_ERROR_RELOAD_AT_KEY);
  if (!value) {
    return 0;
  }

  const timestamp = Number(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const hasReloadedInCurrentSession = (): boolean =>
  safeSessionStorage.getItem(CHUNK_LOAD_ERROR_SESSION_RELOAD_KEY) === '1';

export const shouldReloadForChunkLoadError = (now = getNow()): boolean =>
  !hasReloadedInCurrentSession() && now - getLastReloadAt() >= CHUNK_LOAD_ERROR_RELOAD_COOLDOWN;

export const markChunkLoadErrorReload = (now = getNow()): boolean => {
  const isReloadAtStored = safeStorage.setItem(CHUNK_LOAD_ERROR_RELOAD_AT_KEY, String(now));
  const isSessionReloadStored = safeSessionStorage.setItem(CHUNK_LOAD_ERROR_SESSION_RELOAD_KEY, '1');

  return isReloadAtStored && isSessionReloadStored;
};

export const tryRecoverChunkLoadError = (windowTarget: WindowLike = window): boolean => {
  if (!shouldReloadForChunkLoadError()) {
    return false;
  }

  if (!markChunkLoadErrorReload()) {
    return false;
  }

  windowTarget.location.reload();
  return true;
};

const getErrorMessage = (error: ErrorEvent): string | undefined => error.error?.message ?? error.message;

const getRejectionMessage = (event: PromiseRejectionEvent): string | undefined => {
  const { reason } = event;

  if (typeof reason === 'string') {
    return reason;
  }

  if (reason instanceof Error) {
    return reason.message;
  }

  if (typeof reason === 'object' && reason !== null && 'message' in reason && typeof reason.message === 'string') {
    return reason.message;
  }

  return undefined;
};

const handleChunkLoadError = (windowTarget: WindowLike, message?: string): void => {
  if (isChunkLoadError(message)) {
    tryRecoverChunkLoadError(windowTarget);
  }
};

export const setupChunkLoadErrorRecovery = (windowTarget: WindowLike = window): (() => void) => {
  if (process.env.NEXT_PUBLIC_DEPLOY_ENV !== 'prod') {
    return () => {};
  }

  const onError = (event: ErrorEvent) => {
    handleChunkLoadError(windowTarget, getErrorMessage(event));
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    handleChunkLoadError(windowTarget, getRejectionMessage(event));
  };

  windowTarget.addEventListener('error', onError);
  windowTarget.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    windowTarget.removeEventListener('error', onError);
    windowTarget.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
};
